/**
 * Example RBAC Page
 * 
 * This page demonstrates how to use the Role-Based Access Control system
 * in PBS. It shows various permission checks and conditional rendering.
 */

import React, { useState } from 'react';
import PermissionGuard, { usePermissions, RoleBadge } from '../components/PermissionGuard';
import { Permission, Role } from '../services/permissionService';

const ExampleRBACPage: React.FC = () => {
  const {
    hasPermission,
    hasRole,
    canModifyAllocation,
    canDeleteAllocation,
    getRoleDisplayName,
    isFutureDate
  } = usePermissions();

  // Example data
  const [allocations] = useState([
    { id: 1, month: '2023-11', member: 'John Doe', hours: 160 },  // Past
    { id: 2, month: '2024-01', member: 'Jane Smith', hours: 120 },  // Current
    { id: 3, month: '2024-12', member: 'Bob Wilson', hours: 80 },   // Future
  ]);

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header with Role Badge */}
      <div className="bg-white shadow-sm rounded-lg p-6 mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">RBAC Example Page</h1>
            <p className="text-gray-600 mt-2">
              Demonstrating Role-Based Access Control features
            </p>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-600 mb-2">Your Role:</p>
            <RoleBadge className="text-lg" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Example 1: Simple Permission Check */}
        <div className="bg-white shadow-sm rounded-lg p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Example 1: Revenue Entry
          </h2>
          <p className="text-gray-600 mb-4">
            Only users with <code className="bg-gray-100 px-2 py-1 rounded">ENTER_REVENUE</code> permission can see this form.
          </p>

          <PermissionGuard 
            permission={Permission.ENTER_REVENUE}
            fallback={
              <div className="text-red-600 bg-red-50 p-4 rounded-lg">
                ❌ You don't have permission to enter revenue data.
              </div>
            }
          >
            <div className="bg-green-50 p-4 rounded-lg">
              <p className="text-green-800 font-medium mb-3">
                ✅ You can enter revenue!
              </p>
              <input
                type="number"
                placeholder="Enter monthly revenue"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
              <button className="mt-3 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                Save Revenue
              </button>
            </div>
          </PermissionGuard>
        </div>

        {/* Example 2: Role-Based Section */}
        <div className="bg-white shadow-sm rounded-lg p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Example 2: Project Health Dashboard
          </h2>
          <p className="text-gray-600 mb-4">
            Only <code className="bg-gray-100 px-2 py-1 rounded">BU_HEAD</code> and{' '}
            <code className="bg-gray-100 px-2 py-1 rounded">OPERATIONS</code> can view this.
          </p>

          <PermissionGuard 
            roles={[Role.BU_HEAD, Role.OPERATIONS]}
            fallback={
              <div className="text-yellow-700 bg-yellow-50 p-4 rounded-lg">
                ⚠️ This section is only available to BU Heads and Operations.
              </div>
            }
          >
            <div className="bg-purple-50 p-4 rounded-lg">
              <p className="text-purple-800 font-medium mb-3">
                ✅ Project Health Dashboard
              </p>
              <div className="space-y-2">
                <div className="flex justify-between items-center p-2 bg-white rounded">
                  <span className="text-gray-700">Project A</span>
                  <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm">
                    Healthy
                  </span>
                </div>
                <div className="flex justify-between items-center p-2 bg-white rounded">
                  <span className="text-gray-700">Project B</span>
                  <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm">
                    At Risk
                  </span>
                </div>
              </div>
            </div>
          </PermissionGuard>
        </div>

        {/* Example 3: Date-Based Permissions */}
        <div className="bg-white shadow-sm rounded-lg p-6 md:col-span-2">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Example 3: Allocation Management (Date-Based Permissions)
          </h2>
          <p className="text-gray-600 mb-4">
            Project Owners can only modify <strong>future</strong> allocations.
            Operations can modify both past and future.
          </p>

          <div className="space-y-3">
            {allocations.map((allocation) => {
              const isFuture = isFutureDate(allocation.month);
              const canModify = canModifyAllocation(allocation.month);
              const canDelete = canDeleteAllocation(allocation.month);

              return (
                <div
                  key={allocation.id}
                  className={`flex items-center justify-between p-4 rounded-lg border-2 ${
                    isFuture ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-gray-50'
                  }`}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <span className="font-medium text-gray-900">{allocation.month}</span>
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          isFuture
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-200 text-gray-700'
                        }`}
                      >
                        {isFuture ? 'Future' : 'Past'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">
                      {allocation.member} - {allocation.hours} hours
                    </p>
                  </div>

                  <div className="flex items-center gap-2">
                    {canModify ? (
                      <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm">
                        Edit
                      </button>
                    ) : (
                      <button
                        className="px-4 py-2 bg-gray-300 text-gray-500 rounded-lg cursor-not-allowed text-sm"
                        disabled
                        title="Cannot modify past allocations"
                      >
                        Edit
                      </button>
                    )}

                    {canDelete ? (
                      <button className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm">
                        Delete
                      </button>
                    ) : (
                      <button
                        className="px-4 py-2 bg-gray-300 text-gray-500 rounded-lg cursor-not-allowed text-sm"
                        disabled
                        title="Cannot delete past allocations"
                      >
                        Delete
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Add button */}
          <PermissionGuard permission={Permission.ADD_FUTURE_ALLOCATIONS} hideIfNoPermission>
            <button className="mt-4 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 w-full font-medium">
              + Add New Allocation
            </button>
          </PermissionGuard>
        </div>

        {/* Example 4: Multiple Permissions */}
        <div className="bg-white shadow-sm rounded-lg p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Example 4: View All Costs
          </h2>
          <p className="text-gray-600 mb-4">
            Requires either <code className="bg-gray-100 px-2 py-1 rounded">VIEW_COSTS</code> or{' '}
            <code className="bg-gray-100 px-2 py-1 rounded">VIEW_ALL_COSTS</code>
          </p>

          <PermissionGuard
            permissions={[Permission.VIEW_COSTS, Permission.VIEW_ALL_COSTS]}
            fallback={
              <div className="text-gray-600 bg-gray-50 p-4 rounded-lg">
                You don't have permission to view costs.
              </div>
            }
          >
            <div className="bg-blue-50 p-4 rounded-lg">
              <p className="text-blue-800 font-medium mb-3">Cost Summary</p>
              <div className="space-y-2">
                <div className="flex justify-between p-2 bg-white rounded">
                  <span>Labor Costs</span>
                  <span className="font-semibold">$125,000</span>
                </div>
                <div className="flex justify-between p-2 bg-white rounded">
                  <span>Infrastructure</span>
                  <span className="font-semibold">$35,000</span>
                </div>
                <div className="flex justify-between p-2 bg-white rounded border-t-2 border-gray-300">
                  <span className="font-bold">Total</span>
                  <span className="font-bold text-blue-600">$160,000</span>
                </div>
              </div>
            </div>
          </PermissionGuard>
        </div>

        {/* Example 5: usePermissions Hook */}
        <div className="bg-white shadow-sm rounded-lg p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Example 5: Using the Hook
          </h2>
          <p className="text-gray-600 mb-4">
            The <code className="bg-gray-100 px-2 py-1 rounded">usePermissions</code> hook
            provides functions for inline checks.
          </p>

          <div className="bg-gray-50 p-4 rounded-lg space-y-2 text-sm font-mono">
            <div className="flex justify-between items-center p-2 bg-white rounded">
              <span>Your Role:</span>
              <span className="font-bold text-blue-600">{getRoleDisplayName()}</span>
            </div>
            <div className="flex justify-between items-center p-2 bg-white rounded">
              <span>Can Enter Revenue:</span>
              <span className={hasPermission(Permission.ENTER_REVENUE) ? 'text-green-600' : 'text-red-600'}>
                {hasPermission(Permission.ENTER_REVENUE) ? '✅ Yes' : '❌ No'}
              </span>
            </div>
            <div className="flex justify-between items-center p-2 bg-white rounded">
              <span>Can View Project Health:</span>
              <span className={hasPermission(Permission.VIEW_PROJECT_HEALTH) ? 'text-green-600' : 'text-red-600'}>
                {hasPermission(Permission.VIEW_PROJECT_HEALTH) ? '✅ Yes' : '❌ No'}
              </span>
            </div>
            <div className="flex justify-between items-center p-2 bg-white rounded">
              <span>Can View All Projects:</span>
              <span className={hasPermission(Permission.VIEW_ALL_PROJECTS) ? 'text-green-600' : 'text-red-600'}>
                {hasPermission(Permission.VIEW_ALL_PROJECTS) ? '✅ Yes' : '❌ No'}
              </span>
            </div>
            <div className="flex justify-between items-center p-2 bg-white rounded">
              <span>Can Add Members:</span>
              <span className={hasPermission(Permission.ADD_MEMBERS) ? 'text-green-600' : 'text-red-600'}>
                {hasPermission(Permission.ADD_MEMBERS) ? '✅ Yes' : '❌ No'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Role-Specific Messages */}
      <div className="mt-6 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-3">Role-Specific Information</h3>
        
        {hasRole(Role.PROJECT_OWNER) && (
          <div className="bg-white p-4 rounded-lg shadow-sm">
            <p className="text-gray-800">
              <strong>As a Project Owner:</strong> You can manage allocations for your projects,
              enter revenue data, and add/modify team members. You cannot modify past allocations.
            </p>
          </div>
        )}

        {hasRole(Role.BU_HEAD) && (
          <div className="bg-white p-4 rounded-lg shadow-sm">
            <p className="text-gray-800">
              <strong>As a BU Head:</strong> You have full visibility into all projects,
              can view the Project Health dashboard, and enter revenue data across all projects.
            </p>
          </div>
        )}

        {hasRole(Role.OPERATIONS) && (
          <div className="bg-white p-4 rounded-lg shadow-sm">
            <p className="text-gray-800">
              <strong>As Operations:</strong> You have full access to all features including
              modifying past allocations and complete project management capabilities.
            </p>
          </div>
        )}

        {hasRole(Role.ACCOUNTS) && (
          <div className="bg-white p-4 rounded-lg shadow-sm">
            <p className="text-gray-800">
              <strong>As Accounts:</strong> You can view all project costs and revenue data
              for financial tracking and reporting purposes.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExampleRBACPage;


